// client/src/components/Recurring/RecurringTemplatesPage.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Grid,
  Divider,
  Stack,
  Tooltip,
  Paper
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Visibility as PreviewIcon,
  CalendarToday as CalendarIcon,
  Repeat as RepeatIcon
} from '@mui/icons-material';
import { useRecurringTransactions } from '../../contexts/RecurringTransactionContext';
import { formatCurrency } from '../../utils/helpers';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../contexts/CurrencyContext';

const RecurringTemplatesPage = () => {
  const { currency } = useCurrency(); // eslint-disable-line no-unused-vars
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    recurringTransactions,
    loading,
    error,
    fetchRecurringTransactions,
    updateRecurringTransaction,
    deleteRecurringTransaction,
    previewRecurring
  } = useRecurringTransactions();

  const [filter, setFilter] = useState('all'); // 'all', 'active', 'paused'
  const [previewDialog, setPreviewDialog] = useState({ open: false, template: null, preview: [] });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, template: null });
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    fetchRecurringTransactions();
  }, [fetchRecurringTransactions]);

  const filteredTemplates = recurringTransactions.filter(template => {
    if (filter === 'active') return template.recurringConfig?.isActive !== false;
    if (filter === 'paused') return template.recurringConfig?.isActive === false;
    return true; // 'all'
  });

  const handlePauseToggle = async (template) => {
    try {
      const isActive = template.recurringConfig?.isActive !== false;
      await updateRecurringTransaction(template._id, {
        recurringConfig: {
          ...template.recurringConfig,
          isActive: !isActive
        }
      });
      fetchRecurringTransactions();
    } catch (err) {
      console.error('Failed to toggle template status:', err);
    }
  };

  const handlePreview = async (template) => {
    setLoadingPreview(true);
    try {
      const preview = await previewRecurring({
        amount: template.amount,
        frequency: template.recurringConfig?.frequency || 'monthly',
        startDate: template.recurringConfig?.nextOccurrence || template.date,
        endDate: template.recurringConfig?.endDate,
        count: 6 // Show next 6 occurrences
      });
      setPreviewDialog({ open: true, template, preview });
    } catch (err) {
      console.error('Failed to generate preview:', err);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteRecurringTransaction(deleteDialog.template._id, {
        deleteInstances: true // Also delete generated instances
      });
      setDeleteDialog({ open: false, template: null });
      fetchRecurringTransactions();
    } catch (err) {
      console.error('Failed to delete template:', err);
    }
  };

  const getFrequencyLabel = (frequency) => {
    const labels = {
      daily: t('recurring.frequency.daily', 'Daily'),
      weekly: t('recurring.frequency.weekly', 'Weekly'),
      biweekly: t('recurring.frequency.biweekly', 'Bi-weekly'),
      monthly: t('recurring.frequency.monthly', 'Monthly'),
      yearly: t('recurring.frequency.yearly', 'Yearly')
    };
    return labels[frequency] || frequency;
  };

  if (loading && recurringTransactions.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('recurring.templates.title', 'Recurring Templates')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('recurring.templates.subtitle', 'Manage your recurring income and expenses')}
        </Typography>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(e, newFilter) => newFilter && setFilter(newFilter)}
          size="small"
        >
          <ToggleButton value="all">
            {t('recurring.filter.all', 'All')} ({recurringTransactions.length})
          </ToggleButton>
          <ToggleButton value="active">
            {t('recurring.filter.active', 'Active')} ({recurringTransactions.filter(t => t.recurringConfig?.isActive !== false).length})
          </ToggleButton>
          <ToggleButton value="paused">
            {t('recurring.filter.paused', 'Paused')} ({recurringTransactions.filter(t => t.recurringConfig?.isActive === false).length})
          </ToggleButton>
        </ToggleButtonGroup>

        <Button
          variant="contained"
          onClick={() => navigate('/dashboard/transactions')}
        >
          {t('recurring.templates.addNew', 'Add New Template')}
        </Button>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Empty State */}
      {filteredTemplates.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <RepeatIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {filter === 'all' 
              ? t('recurring.empty.title', 'No recurring templates yet')
              : t('recurring.empty.filtered', `No ${filter} templates`)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('recurring.empty.subtitle', 'Create recurring templates to automate your regular income and expenses')}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/dashboard/transactions')}
          >
            {t('recurring.empty.action', 'Create Your First Template')}
          </Button>
        </Paper>
      )}

      {/* Templates Grid */}
      <Grid container spacing={2}>
        {filteredTemplates.map((template) => {
          const isActive = template.recurringConfig?.isActive !== false;
          const nextOccurrence = template.recurringConfig?.nextOccurrence;
          const frequency = template.recurringConfig?.frequency || 'monthly';

          return (
            <Grid item xs={12} sm={6} md={4} key={template._id}>
              <Card 
                sx={{ 
                  height: '100%',
                  opacity: isActive ? 1 : 0.6,
                  borderLeft: 4,
                  borderColor: template.type === 'income' ? 'success.main' : 'error.main'
                }}
              >
                <CardContent>
                  {/* Status Chip */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Chip
                      label={isActive ? t('recurring.status.active', 'Active') : t('recurring.status.paused', 'Paused')}
                      color={isActive ? 'success' : 'default'}
                      size="small"
                    />
                    <Chip
                      label={template.type === 'income' ? t('transaction.type.income', 'Income') : t('transaction.type.expense', 'Expense')}
                      color={template.type === 'income' ? 'success' : 'error'}
                      size="small"
                      variant="outlined"
                    />
                  </Box>

                  {/* Description */}
                  <Typography variant="h6" gutterBottom noWrap>
                    {template.description}
                  </Typography>

                  {/* Amount */}
                  <Typography 
                    variant="h5" 
                    color={template.type === 'income' ? 'success.main' : 'error.main'}
                    sx={{ mb: 2 }}
                  >
                    {template.type === 'income' ? '+' : '-'}{formatCurrency(template.amount)}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  {/* Details */}
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RepeatIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {getFrequencyLabel(frequency)}
                      </Typography>
                    </Box>
                    
                    {nextOccurrence && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {t('recurring.nextOccurrence', 'Next')}: {format(parseISO(nextOccurrence), 'MMM dd, yyyy')}
                        </Typography>
                      </Box>
                    )}

                    {template.categoryId?.name && (
                      <Chip
                        label={template.categoryId.name}
                        size="small"
                        sx={{
                          bgcolor: template.categoryId.color || '#ccc',
                          color: '#fff',
                          width: 'fit-content'
                        }}
                      />
                    )}
                  </Stack>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Box>
                    <Tooltip title={isActive ? t('recurring.action.pause', 'Pause') : t('recurring.action.resume', 'Resume')}>
                      <IconButton
                        size="small"
                        onClick={() => handlePauseToggle(template)}
                        color={isActive ? 'warning' : 'success'}
                      >
                        {isActive ? <PauseIcon /> : <PlayIcon />}
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={t('recurring.action.preview', 'Preview')}>
                      <IconButton
                        size="small"
                        onClick={() => handlePreview(template)}
                        disabled={loadingPreview}
                      >
                        <PreviewIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Box>
                    <Tooltip title={t('recurring.action.edit', 'Edit')}>
                      <IconButton
                        size="small"
                        onClick={() => navigate('/dashboard/transactions', { state: { editTemplate: template } })}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={t('recurring.action.delete', 'Delete')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteDialog({ open: true, template })}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, template: null, preview: [] })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('recurring.preview.title', 'Preview Future Occurrences')}
        </DialogTitle>
        <DialogContent>
          {previewDialog.template && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                {previewDialog.template.description}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {formatCurrency(previewDialog.template.amount)} • {getFrequencyLabel(previewDialog.template.recurringConfig?.frequency)}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <List dense>
                {previewDialog.preview.map((occurrence, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={format(parseISO(occurrence.date), 'MMMM dd, yyyy')}
                      secondary={formatCurrency(occurrence.amount)}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog({ open: false, template: null, preview: [] })}>
            {t('common.close', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, template: null })}
      >
        <DialogTitle>
          {t('recurring.delete.title', 'Delete Recurring Template?')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('recurring.delete.message', 'Are you sure you want to delete this recurring template? This will also delete all future instances that haven\'t occurred yet.')}
          </Typography>
          {deleteDialog.template && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2">
                {deleteDialog.template.description}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatCurrency(deleteDialog.template.amount)} • {getFrequencyLabel(deleteDialog.template.recurringConfig?.frequency)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, template: null })}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RecurringTemplatesPage;
